/* eslint-disable max-len */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { currency } from 'Helpers/Formats';
import { healthSavingsAccounts as enumeratedValuesHealthSavingsAccounts } from 'Consts/EnumeratedValues';
import Currency from 'Components/Format/Currency';
import EditButtonLink from 'Components/Blueprint/UI/EditButtonLink';

const HealthInsurance = ({
  medicareInsurancePolicies,
  healthSavingsAccounts,
  flexibleSpendingAccounts,
  privateHealthInsurancePolicies,
  shortTermDisabilityInsurancePolicies,
  longTermDisabilityInsurancePolicies,
  privateDisabilityInsurancePolicies,
  longTermCareInsurancePolicies,
  groupHealthInsuranceDetails,
  editLink,
}) => (
  <tr className="health-insurance-etc">
    <td className="health-insurance">
      <div className="icon">
        <div className="icon-health-insurance" />
      </div>
      <h3>
        Health Insurance
        {editLink && (<EditButtonLink
          title="Edit Health Insurance"
          to={editLink}
        />)}
      </h3>

      {/*
      |--------------------------------------------------------------------------
      | Medicare
      | Health Savings
      | Flexible Spending (This is Group Health Insurance)
      | Private Health Insurance
      |--------------------------------------------------------------------------
      */}
      {medicareInsurancePolicies?.length > 0
      || healthSavingsAccounts?.length > 0
      || flexibleSpendingAccounts?.length > 0
      || privateHealthInsurancePolicies?.length > 0 ? (
        <Fragment>
          {medicareInsurancePolicies.map(policy => (
            <div className="item">
              <span className="name">
                Medicare
              </span>
              <br />
              <div key={`${policy.id}policy`}>
                {policy.attributes.otherType}
                <br />
                {policy.attributes.insuredClientName}
              </div>
            </div>
          ))}
          {flexibleSpendingAccounts?.length > 0
          && flexibleSpendingAccounts.map(account => (
            <div className="item" key={`${account.id}fsa`}>
              <span className="name">
                Group Health
              </span>
              <br />
              {groupHealthInsuranceDetails && (
                <div>
                  {groupHealthInsuranceDetails}
                  <br />
                </div>
              )}
              {`${enumeratedValuesHealthSavingsAccounts(
                'fsaType',
                account.attributes.FSAType,
              )} FSA`}
              {account.attributes.eligibleForInvestment && ' - Investment Eligible'}
              <br />
              <Fragment>
                {account.attributes.balance ? currency(account.attributes.balance, true) : '$0'}
                <span> &middot; </span>
                {account.attributes.annualContribution ? currency(account.attributes.annualContribution, true) : '$0'}
                <span>/yr</span>
              </Fragment>
              <br />
              {account.attributes.institution !== '' && (
                <Fragment>{account.attributes.institution}</Fragment>
              )}
            </div>
          ))}
          {privateHealthInsurancePolicies?.length > 0
          && privateHealthInsurancePolicies.map(account => (
            <div className="item" key={`${account.id}phi`}>
              <span className="name">Private Health</span>
              <br />
              {account.attributes.insuredClientName !== '' && (
                <Fragment>
                  <span className="insured">
                    {account.attributes.insuredClientName}
                  </span>
                  <br />
                </Fragment>
              )}
              {account.attributes.provider !== '' && (
                <Fragment>
                  {account.attributes.provider}
                  <br />
                </Fragment>
              )}
              {account.attributes.hsaEligible && (
                <div>
                  <Fragment>HSA - Investment Eligible</Fragment>
                  <br />
                </div>
              )}
              <Fragment>
                {account.attributes.balance ? currency(account.attributes.balance, true) : '$0'}
                <span> &middot; </span>
                {account.attributes.annualContribution ? currency(account.attributes.annualContribution, true) : '$0'}
                <span>/yr</span>
              </Fragment>
              <br />
              {account.attributes.institution !== '' && (
                <Fragment>{account.attributes.institution}</Fragment>
              )}
            </div>
          ))}
        </Fragment>
        ) : (
          <div>No health insurance.</div>
        )}
    </td>
    {/*
      |--------------------------------------------------------------------------
      | Disability Insurance
      |--------------------------------------------------------------------------
      */}
    <td className="disability-insurance">
      <div className="icon">
        <div className="icon-disability-insurance" />
      </div>
      <h3>
        Disability
        {editLink && (<EditButtonLink
          title="Edit Disability Insurance"
          to={editLink}
        />)}
      </h3>
      {shortTermDisabilityInsurancePolicies?.length > 0
      || longTermDisabilityInsurancePolicies?.length > 0
      || privateDisabilityInsurancePolicies?.length > 0 ? (
        <div className="items">
          {shortTermDisabilityInsurancePolicies?.length > 0
            && shortTermDisabilityInsurancePolicies.map(account => (
              <div className="item" key={`${account.id}std`}>
                <span className="name">
                  STD -&nbsp;
                  {account.attributes.insuredClientName}
                </span>
                <br />
                <span>{account.attributes.insuredClientName}</span>
                <br />
                {account.attributes.benefit > 0 && (
                  <Fragment>
                    <Currency amount={account.attributes.benefit} />
                    <span>/month</span>
                  </Fragment>
                )}
              </div>
            ))}
          {longTermDisabilityInsurancePolicies?.length > 0
            && longTermDisabilityInsurancePolicies.map(account => (
              <div className="item" key={`${account.id}ltd`}>
                <span className="name">
                  LTD -&nbsp;
                  {account.attributes.insuredClientName}
                </span>
                <br />
                <span>{account.attributes.insuredClientName}</span>
                <br />
                {account.attributes.benefit > 0 && (
                  <Fragment>
                    <Currency amount={account.attributes.benefit} />
                    <span>/month</span>
                    <br />
                  </Fragment>
                )}
                {account.attributes.maximumBenefit > 0 && (
                  <Fragment>
                    Max&nbsp;
                    <Currency amount={account.attributes.maximumBenefit} />
                    <span>/month</span>
                    <br />
                  </Fragment>
                )}
                {account.attributes.coveragePercent > 0 && (
                  <Fragment>
                    {`${account.attributes.coveragePercent}% Coverage`}
                    <br />
                  </Fragment>
                )}
                {account.attributes.coverageTypeReadonlyPrompt !== '' && (
                  <Fragment>
                    {account.attributes.coverageTypeReadonlyPrompt}
                  </Fragment>
                )}
              </div>
            ))}
          {privateDisabilityInsurancePolicies?.length > 0
            && privateDisabilityInsurancePolicies.map(account => (
              <div className="item" key={`${account.id}pdi`}>
                <span className="name">
                  Private -&nbsp;
                  {account.attributes.company}
                </span>
                <br />
                <span>{account.attributes.company}</span>
                <br />
                {account.attributes.benefit > 0 && (
                  <Fragment>
                    <Currency amount={account.attributes.benefit} />
                    <span>/month</span>
                    <br />
                  </Fragment>
                )}
                <span>{account.attributes.insuredClientName}</span>
                <br />
              </div>
            ))}
        </div>
        ) : (
          <div>No disability insurance.</div>
        )}
    </td>
    {/*
      |--------------------------------------------------------------------------
      | Long-Term Care
      |--------------------------------------------------------------------------
      */}
    <td className="long-term-care">
      <div className="icon">
        <div className="icon-long-term-care" />
      </div>
      <h3>
        Long-Term Care
        {editLink && (<EditButtonLink
          title="Edit Long-Term Care Insurance"
          to={editLink}
        />)}
      </h3>
      {longTermCareInsurancePolicies?.length > 0 ? (
        <Fragment>
          {longTermCareInsurancePolicies?.length > 0
            && longTermCareInsurancePolicies.map(account => (
              <div className="item" key={`${account.id}ltci`}>
                <span className="name">{account.attributes.insuredClientName}</span>
                <br />
                <span>{account.attributes.company}</span>
                <br />
                {account.attributes.benefit > 0 && (
                  <Fragment>
                    Monthly Benefit:&nbsp;
                    <Currency amount={account.attributes.benefit} />
                    <span>/month</span>
                    <br />
                  </Fragment>
                )}
                {account.attributes.period !== '' && (
                  <Fragment>
                    {account.attributes.period}
                    &nbsp;year(s)
                  </Fragment>
                )}
              </div>
            ))}
        </Fragment>
      ) : (
        <div>No long-term care insurance.</div>
      )}
    </td>
  </tr>
);

HealthInsurance.propTypes = {
  medicareInsurancePolicies: PropTypes.array.isRequired,
  healthSavingsAccounts: PropTypes.array.isRequired,
  flexibleSpendingAccounts: PropTypes.array.isRequired,
  privateHealthInsurancePolicies: PropTypes.array.isRequired,
  shortTermDisabilityInsurancePolicies: PropTypes.array.isRequired,
  longTermDisabilityInsurancePolicies: PropTypes.array.isRequired,
  privateDisabilityInsurancePolicies: PropTypes.array.isRequired,
  longTermCareInsurancePolicies: PropTypes.array.isRequired,
  groupHealthInsuranceDetails: PropTypes.string,
  editLink: PropTypes.string,
};
HealthInsurance.defaultProps = {
  editLink: '',
  groupHealthInsuranceDetails: null,
};

export default HealthInsurance;
